import { NextApiRequest, NextApiResponse } from 'next';
import { AxiosError, AxiosResponse } from 'axios';
import logger from './logger';
import { handleAclErrorResponse } from './ssErrorHandler';

interface Record {
    [key: string]: any;
}

export const getData = async <T extends Record>(
    res: NextApiResponse,
    req: NextApiRequest,
    callApi: Promise<AxiosResponse>,
    expectedStatusCode = 200
): Promise<T> => {
    try {
        const result = await callApi;
        const { status, data, statusText } = result;
        if (status === 204) {
            return null;
        }
        if (status !== expectedStatusCode || !data) {
            throw new Error(statusText);
        }

        return data;
    } catch (e: any) {
        await handleError(res, req, e);
    }
};

export const getDataData = async <T extends Record>(
    res: NextApiResponse,
    req: NextApiRequest,
    callApi: Promise<AxiosResponse>,
    expectedStatusCode = 200
): Promise<T> => {
    const resData = await getData(res, req, callApi, expectedStatusCode);
    return resData?.data;
};

const handleError = async (
    res: NextApiResponse,
    req: NextApiRequest,
    e: AxiosError
) => {
    logError(e);
    handleAclErrorResponse(res, req, e);

    const err = new Error('An error occurred at');

    if (e?.response?.status === 503) {
        logger.warn('Server Maintenance');
        throw '';
    } else if (process.env.NODE_ENV === 'development') {
        throw err;
    } else throw JSON.stringify(err, Object.getOwnPropertyNames(err));
};

export const sendApiRequest = async (
    callApi: Promise<AxiosResponse>,
    res: NextApiResponse
): Promise<void> => {
    try {
        const response = await callApi;
        if (!response) {
            console.error(
                'The API response was undefined. It could be an auth problem. Maybe your endpoint should be anonymous?'
            );
            return;
        }
        res.statusCode = response.status;
        res.json(response.data);
    } catch (error: any) {
        res.statusCode = error.response?.status;
        logError(error);
        res.json({
            data: {
                on: 'FACR API',
                code: error.response?.data?.message,
                errors: error.response?.data?.errors,
            },
        });
    } finally {
        res.end();
    }
};

export const logError = (e: AxiosError<any>) => {
    // Response is not returned on connection error
    if (typeof e.response === 'undefined') {
        logger.error(e.message, 'error while calling Backend API');
        return;
    }

    logger.error(
        {
            code: e.response?.status || 500,
            request: {
                url: e.response.config.baseURL + '/' + e.response.config.url,
                method: e.response.config.method,
                data: e.response.config.data,
                headers: {
                    accept: e.response.config.headers.Accept,
                    authorization: e.response.config.headers.Authorization,
                    contentType: e.response.config.headers['Content-Type'],
                },
            },
            'response-data': {
                ...(e.response.data || {}),
                errors: JSON.stringify(e.response.data?.errors || []),
            },
        },
        'error while calling Backend API'
    );
};
